import type { DocumentBlock, DocumentBlockKind, DocumentContent } from '@morph/spec';
import { validateDocumentContent } from '@morph/spec';
import { OWNS_EDITING_ATTR } from './edit.js';
import type { EditAnchor, ObjectHandle } from './edit.js';

export interface DocumentCaretAnchor {
  readonly edit: EditAnchor;
  readonly block: string;
  readonly offset: number;
  readonly affinity: 'forward' | 'backward';
  readonly before?: string;
  readonly after?: string;
}

export interface DocumentCanvasOptions {
  readonly root: Element;
  readonly edit: EditAnchor;
  readonly content: DocumentContent;
  readonly createBlockId: () => string;
  readonly onChange: (content: DocumentContent, caret: DocumentCaretAnchor) => void | Promise<void>;
  readonly onRefusal?: (error: unknown) => void;
  /**
   * Whether this canvas accepts edits. Defaults to `true`.
   *
   * Decision 0028: *"Read-only rendering is a mode of the canonical canvas, not
   * a fallback to the generated scalar field."* Write authority decides whether
   * the blocks are EDITABLE; it does not decide which representation is
   * rendered. Before this, `?viewer=reader` skipped the canvas entirely and got
   * the generated legacy `body` scalar — prose frozen at migration, while the
   * document it claims to be had moved on. Read-only STALE content is still
   * wrong content.
   *
   * `reconcile` deliberately still works in this mode. A reader has to see what
   * another tab just wrote; refusing to update a read-only view would be the
   * stale-scalar defect again with a shorter fuse.
   */
  readonly editable?: boolean;
}

export interface DocumentCanvas {
  content(): DocumentContent;
  reconcile(content: DocumentContent): DocumentReconciliation;
  insert(after: string, kind?: Exclude<DocumentBlockKind, 'list-item'>): string;
  /** Turn a text block into a paragraph, a heading, a quote or a list item,
   *  keeping its text — what a style gallery does to the caret's block (Word's
   *  Normal / Heading / bullets; the same change the `# `, `- ` and `> `
   *  gestures make at the start of an empty paragraph). A list item made a
   *  paragraph, heading or quote leaves its list: the list's only item takes
   *  the list's place among the roots; otherwise it follows the list.
   *
   *  @Codex's P2 on `daafee9`: the implementation accepted `quote` while THIS
   *  signature did not, so a typed consumer got a compile error for the
   *  conversion the vocabulary had just gained — a new kind reachable from
   *  inside the closure and from nowhere else. The interface is the contract;
   *  widening the implementation alone widens nothing. */
  convert(block: string, kind: 'paragraph' | 'heading' | 'quote' | 'list-item'): void;
  reorder(block: string, before?: string): void;
  restore(anchor: DocumentCaretAnchor): boolean;
  destroy(): void;
}

export interface DocumentConflict {
  readonly path: string;
  readonly basis: unknown;
  readonly local: unknown;
  readonly canonical: unknown;
}

export interface DocumentReconciliation {
  readonly status: 'clean' | 'conflict';
  readonly content: DocumentContent;
  readonly conflicts: readonly DocumentConflict[];
}

const BLOCK = 'data-morph-document-block';

function canonical(content: DocumentContent): DocumentContent {
  const problems = validateDocumentContent(content);
  if (problems.length) throw new TypeError(`invalid document content: ${problems[0]!.path} ${problems[0]!.message}`);
  return { ...content, roots: [...content.roots], blocks: content.blocks.map((block) => ({
    ...block, ...(block.children ? { children: [...block.children] } : {}),
  })) };
}

function same(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * The KINDS a person types into. It says nothing about whether text is
 * PRESENT — @Codex's P2 on `d881f5b`, and the old signature said otherwise.
 *
 * It narrowed to `{ readonly text: string }` on the KIND alone, while
 * `DocumentBlock.text` is optional and a document of `{ id, kind: 'quote' }`
 * validates clean. Reproduced: `validateDocumentContent` returns `[]`, the
 * block renders, and `convert` throws *"Cannot read properties of undefined
 * (reading 'length')"*. Enter throws the same way on `text.slice`.
 *
 * THE REPAIR IS NOT TO REQUIRE THE STRING, which is the other option their
 * finding offers and is the worse one here: an empty quote would then fail
 * this test, so `onInput` would return early and typing into it would do
 * NOTHING, Enter would do nothing, and `convert` would refuse it as "not a
 * text block" — a crash turned into three dead controls, which is what
 * `0193` forbids.
 *
 * An absent text on a textual block means EMPTY, and `textOf` is the one
 * place that says so.
 */
function textBlock(block: DocumentBlock): boolean {
  return block.kind === 'paragraph' || block.kind === 'heading'
    || block.kind === 'quote' || block.kind === 'list-item';
}

/** A textual block's content, with absent read as empty. Every consumer goes
 *  through this rather than through `?? ''` at each site, so a fourth reader
 *  cannot reintroduce the throw by forgetting one. */
function textOf(block: DocumentBlock): string {
  return typeof block.text === 'string' ? block.text : '';
}

/**
 * Once authored structure changes, the legacy blank-line envelope no longer
 * describes it. Keeping the old separator count makes an otherwise valid
 * Enter/insert/reorder fail validation on the ordinary migrated document.
 */
function structural(content: DocumentContent): Omit<DocumentContent, 'legacySeparators'> {
  const { legacySeparators: _legacySeparators, ...canonicalContent } = content;
  return canonicalContent;
}

/** Read what a person sees, including browser-created line breaks. */
function editableText(element: Element): string {
  const rendered = (element as HTMLElement).innerText;
  return typeof rendered === 'string'
    ? rendered.replace(/\r\n?/g, '\n')
    : element.textContent ?? '';
}

function offsetOf(element: Element, node: Node, nodeOffset: number): number {
  const range = element.ownerDocument.createRange();
  range.selectNodeContents(element);
  range.setEnd(node, nodeOffset);
  const holder = element.ownerDocument.createElement('div');
  holder.appendChild(range.cloneContents());
  return editableText(holder).length;
}

function caretOffset(element: Element, selection: Selection | null | undefined): number {
  if (!selection?.anchorNode || !element.contains(selection.anchorNode)) {
    return editableText(element).length;
  }
  return offsetOf(element, selection.anchorNode, selection.anchorOffset);
}

/**
 * What a split replaces, in text-offset terms, low end first.
 *
 * A caret is the degenerate case where they are equal. @Codex, P2 on #164:
 * Enter read one offset and sliced the canonical string there, so pressing
 * Enter over a SELECTION kept the selected text in one of the two blocks
 * instead of replacing it — and a backward selection kept a different half
 * from a forward one, because `anchor` is where the drag started.
 */
function selectedRange(
  element: Element, selection: Selection | null | undefined,
): readonly [number, number] {
  const caret = caretOffset(element, selection);
  if (!selection?.anchorNode || !selection.focusNode
    || !element.contains(selection.anchorNode) || !element.contains(selection.focusNode)) {
    return [caret, caret];
  }
  const focus = offsetOf(element, selection.focusNode, selection.focusOffset);
  return caret <= focus ? [caret, focus] : [focus, caret];
}

/** A DOM projection of canonical blocks. The DOM is never read to discover structure. */
export function attachDocumentCanvas(options: DocumentCanvasOptions): DocumentCanvas {
  let value = canonical(options.content);
  let basis = value;
  const { root } = options;
  const editable = options.editable !== false;

  const blockById = (id: string) => value.blocks.find((block) => block.id === id);
  const rootsInOrder = () => value.roots.map(blockById).filter((block): block is DocumentBlock => !!block);
  const orderContaining = (id: string): readonly string[] =>
    value.roots.includes(id) ? value.roots
      : value.blocks.find((block) => block.children?.includes(id))?.children ?? [];
  const neighbors = (id: string) => {
    const order = orderContaining(id);
    const at = order.indexOf(id);
    return { ...(at > 0 ? { before: order[at - 1] } : {}),
      ...(at >= 0 && at + 1 < order.length ? { after: order[at + 1] } : {}) };
  };
  const anchor = (id: string, offset: number, affinity: 'forward' | 'backward' = 'forward'):
  DocumentCaretAnchor => ({ edit: options.edit, block: id, offset, affinity, ...neighbors(id) });

  const paintBlock = (block: DocumentBlock): Element => {
    const doc = root.ownerDocument;
    if (block.kind === 'list') {
      const list = doc.createElement('ul');
      list.setAttribute(BLOCK, block.id);
      for (const id of block.children ?? []) {
        const child = blockById(id);
        if (child) list.appendChild(paintBlock(child));
      }
      return list;
    }
    if (block.kind === 'embed' || block.kind === 'media') {
      const embedded = doc.createElement(
        block.kind === 'embed' ? 'm-document-embed' : 'm-document-media');
      embedded.setAttribute(BLOCK, block.id);
      embedded.setAttribute('data-morph-target', block.target!);
      embedded.textContent = block.kind === 'embed' ? 'Embedded object' : 'Media object';
      return embedded;
    }
    // A quote is a BLOCKQUOTE and not a styled paragraph. The element is the
    // authored meaning — decision 0025's rule that this vocabulary describes
    // meaning and never presentation — so a screen reader announces a quotation
    // and a stylesheet has something to key on without a class nobody set.
    const element = doc.createElement(block.kind === 'heading' ? `h${block.level}`
      : block.kind === 'quote' ? 'blockquote'
      : block.kind === 'list-item' ? 'li' : 'p');
    element.setAttribute(BLOCK, block.id);
    // A read-only block is ORDINARY PROSE, not a disabled control. No
    // `contenteditable`, and no `role="textbox"` either: a textbox a screen
    // reader cannot type into is a worse answer than a paragraph, and
    // `aria-readonly` on a textbox would still announce an edit affordance
    // this canvas does not have.
    if (editable) {
      element.setAttribute('contenteditable', 'true');
      if (block.kind === 'paragraph') {
        element.setAttribute('role', 'textbox');
        element.setAttribute('aria-multiline', 'true');
      }
    }
    element.textContent = textOf(block);
    return element;
  };

  /**
   * What a root block's painted form depends on.
   *
   * A `list` renders its children, so its identity for reuse purposes is not
   * the block alone. Anything else is exactly itself.
   */
  const signatureOf = (block: DocumentBlock): string => JSON.stringify(
    block.kind === 'list'
      ? [block, (block.children ?? []).map((id) => blockById(id) ?? null)]
      : [block]);

  /** The signature each root element on screen was painted from. */
  const paintedFrom = new Map<string, string>();

  /**
   * KEYED, not `replaceChildren`.
   *
   * `replaceChildren` removes every child and appends new ones, so a repaint
   * driven by ONE remote block replaced every block element in the canvas — and
   * @Codex, driving the deployed preview, measured the consequence directly:
   *
   * > *"Before tab B changed the second note's block, tab A's first block was
   * > focused. After the remote block arrived, tab A's `document.activeElement`
   * > was the page body."*
   *
   * Decision 0031 lists *"the focused block"* and *"selection direction and
   * caret offset"* among the invariants a repaint preserves, and the host's
   * caret bracket does not cover this: `refreshCanonicalSurfaces` reconciles
   * canvases directly, outside the render path.
   *
   * So a block whose painted form is unchanged keeps its element, and keeps it
   * WHERE IT IS: an element already in the right position is left alone rather
   * than re-inserted, because moving a node is removing it, and removing the
   * focused node is what drops the selection.
   */
  const paint = () => {
    const wanted = rootsInOrder();
    const reusable = new Map<string, Element>();
    for (const child of [...root.children]) {
      const id = child.getAttribute(BLOCK);
      if (id !== null) reusable.set(id, child);
    }

    let cursor = root.firstElementChild;
    const kept = new Set<string>();
    for (const block of wanted) {
      const signature = signatureOf(block);
      const held = paintedFrom.get(block.id) === signature ? reusable.get(block.id) : undefined;
      const element = held ?? paintBlock(block);
      if (cursor === element) cursor = element.nextElementSibling;
      else root.insertBefore(element, cursor);
      paintedFrom.set(block.id, signature);
      kept.add(block.id);
    }

    // Whatever is left after the last wanted block is no longer part of the
    // document. `cursor` is where the walk stopped, so this is the tail.
    while (cursor) {
      const next = cursor.nextElementSibling;
      cursor.remove();
      cursor = next;
    }
    for (const id of [...paintedFrom.keys()]) if (!kept.has(id)) paintedFrom.delete(id);
  };

  const reconcile = (loaded: DocumentContent): DocumentReconciliation => {
    const remote = canonical(loaded);
    const conflicts: DocumentConflict[] = [];
    const choose = <T>(path: string, base: T | undefined, local: T | undefined,
      canonicalValue: T | undefined): T | undefined => {
      if (same(local, base)) return canonicalValue;
      if (same(canonicalValue, base) || same(local, canonicalValue)) return local;
      conflicts.push({ path, basis: base, local, canonical: canonicalValue });
      // A conflict is deliberately a draft: it remains visible but is never
      // represented as canonical or silently replaced by the incoming value.
      return local;
    };

    const baseBlocks = new Map(basis.blocks.map((block) => [block.id, block]));
    const localBlocks = new Map(value.blocks.map((block) => [block.id, block]));
    const remoteBlocks = new Map(remote.blocks.map((block) => [block.id, block]));
    const ids = [...new Set([...remoteBlocks.keys(), ...localBlocks.keys(), ...baseBlocks.keys()])];
    const blocks = ids.flatMap((id) => {
      const block = choose(`blocks.${id}`, baseBlocks.get(id), localBlocks.get(id), remoteBlocks.get(id));
      return block ? [block] : [];
    });
    const chosen = choose('roots', basis.roots, value.roots, remote.roots) ?? remote.roots;

    /**
     * MEMBERSHIP AND EXISTENCE ARE MERGED TOGETHER, or the merge is invalid.
     *
     * @Codex, P1 on #166 against its own 2A: *"If both tabs insert different
     * root blocks from the same basis, block reconciliation retains both new
     * block objects, but the independently reconciled `roots` array records a
     * conflict and keeps only the local root ordering. The remote block is then
     * unreachable, so `canonical(...)` throws instead of returning the promised
     * explicit conflict; on the stale-save path that rejection reaches
     * `publish` and rolls back the local insertion."*
     *
     * Confirmed by reading the validator: `packages/spec/src/document.ts` emits
     * *"block is unreachable"* for a block no root or child names. So the two
     * halves above disagreed — `blocks` merged by identity and kept both, while
     * `roots` chose one array whole — and the person's own insertion was the
     * thing rolled back.
     *
     * The chosen ordering still WINS: this appends only the roots that ordering
     * left stranded, in the order the other side had them. A conflict is still
     * recorded, because it was still a conflict; what changes is that the draft
     * it promises to keep visible is a document that can exist.
     */
    const keep = new Set(blocks.map((block) => block.id));
    const rootIds = chosen.filter((id) => keep.has(id));
    for (const source of [value.roots, remote.roots]) {
      for (const id of source) {
        if (keep.has(id) && !rootIds.includes(id)) rootIds.push(id);
      }
    }
    // A block reachable only as somebody's child stays reachable; one that is
    // now named by nothing at all is dropped rather than left to fail
    // validation, because it belongs to a structure neither side kept.
    const named = new Set(rootIds);
    for (const block of blocks) for (const child of block.children ?? []) named.add(child);
    const reachable = blocks.filter((block) => named.has(block.id));
    const roots = rootIds;

    const legacySeparators = choose('legacySeparators', basis.legacySeparators,
      value.legacySeparators, remote.legacySeparators);
    const merged = canonical({ version: remote.version, roots, blocks: reachable,
      ...(legacySeparators ? { legacySeparators } : {}) });

    /**
     * A SAME-CONTENT reconciliation does not repaint, and that is load-bearing.
     *
     * Decision 0031: *"A same-content reconciliation must be a no-op rather
     * than repainting the block tree."* `paint()` calls `replaceChildren`, so
     * every block becomes a NEW element — and the caret was in the old one.
     * A host that carefully preserves this canvas across a repaint, then
     * reconciles it with content it already has, would undo its own work and
     * leave the person typing into a detached node. That is the defect
     * `watchPool` was suppressing, arriving through the fix for it.
     *
     * The comparison is on the merged value against what is already displayed,
     * not on the incoming content: a merge can be a no-op even when the remote
     * differs, which is exactly the common case of another tab changing a note
     * this canvas is not editing.
     */
    const unchanged = same(merged, value);
    basis = remote;
    value = merged;
    if (!unchanged) paint();
    return { status: conflicts.length ? 'conflict' : 'clean', content: value, conflicts };
  };

  const publish = (next: DocumentContent, block: string, offset: number,
    affinity: 'forward' | 'backward' = 'forward') => {
    const previous = value;
    value = canonical(next);
    const published = value;
    const saved = anchor(block, offset, affinity);
    void Promise.resolve(options.onChange(value, saved)).catch((error) => {
      // A refused durable write must not leave a projection claiming it stuck.
      // Repaint from the last canonical value and surface the conflict.
      if (value === published) {
        value = previous;
        paint();
        restore(saved);
      }
      options.onRefusal?.(error);
    });
  };

  const idFrom = (target: EventTarget | null): string | undefined =>
    target instanceof root.ownerDocument.defaultView!.Element
      ? target.closest(`[${BLOCK}]`)?.getAttribute(BLOCK) ?? undefined : undefined;

  const onInput = (event: Event) => {
    const id = idFrom(event.target);
    const block = id && blockById(id);
    if (!id || !block || !textBlock(block)) return;
    // Stopped at the canvas edge as well as declared with `OWNS_EDITING_ATTR`.
    // The attribute is what keeps the host from CANCELLING the gesture; this
    // keeps any other delegated listener above from reading it as its own.
    event.stopPropagation();
    const element = event.target as Element;
    const text = editableText(element);
    const selection = root.ownerDocument.defaultView?.getSelection();
    const offset = caretOffset(element, selection);

    // Markdown's two smallest structural gestures make the semantic vocabulary
    // reachable without turning the canvas into a toolbar. The shortcut is
    // recognized only at the start of a root paragraph, when the caret is
    // immediately after it; elsewhere the same characters remain prose.
    if (block.kind === 'paragraph' && value.roots.includes(id) && offset === 2
      && (text === '# ' || text === '- ' || text === '> ')) {
      if (text === '# ') {
        const blocks = value.blocks.map((candidate) => candidate.id === id
          ? { id, kind: 'heading' as const, level: 2 as const, text: '' }
          : candidate);
        publish({ ...structural(value), blocks }, id, 0);
      } else if (text === '> ') {
        // A quote needs no container, so this is the heading's shape rather
        // than the list's: the block becomes a quote in place and keeps its
        // position among the roots.
        const blocks = value.blocks.map((candidate) => candidate.id === id
          ? { id, kind: 'quote' as const, text: '' }
          : candidate);
        publish({ ...structural(value), blocks }, id, 0);
      } else {
        const list = options.createBlockId();
        const roots = value.roots.map((rootId) => rootId === id ? list : rootId);
        const blocks = value.blocks.map((candidate) => candidate.id === id
          ? { id, kind: 'list-item' as const, text: '' }
          : candidate);
        publish({ ...structural(value), roots,
          blocks: [...blocks, { id: list, kind: 'list', children: [id] }] }, id, 0);
      }
      paint();
      restore(anchor(id, 0));
      return;
    }

    const blocks = value.blocks.map((candidate) => candidate.id === id ? { ...candidate, text } : candidate);
    publish({ ...value, blocks }, id, offset);
  };

  /** Every door that would publish. A read-only canvas THROWS rather than
   *  quietly doing nothing: a caller reaching for `insert` on a reader's canvas
   *  has a bug, and a silent no-op is how it ships. */
  const mustBeEditable = (gesture: string) => {
    if (!editable) throw new TypeError(`${gesture} is not available on a read-only document canvas`);
  };

  const insert = (after: string, kind: Exclude<DocumentBlockKind, 'list-item'> = 'paragraph'): string => {
    mustBeEditable('insert');
    if (kind === 'embed' || kind === 'media' || kind === 'list')
      throw new TypeError(`${kind} insertion requires semantic payload`);
    const id = options.createBlockId();
    const at = value.roots.indexOf(after);
    if (at < 0) throw new TypeError(`block ${after} is not a root`);
    const block: DocumentBlock = kind === 'heading'
      ? { id, kind, level: 2, text: '' }
      : kind === 'quote' ? { id, kind: 'quote', text: '' }
        : { id, kind: 'paragraph', text: '' };
    const roots = [...value.roots];
    roots.splice(at + 1, 0, id);
    publish({ ...structural(value), roots, blocks: [...value.blocks, block] }, id, 0);
    paint();
    restore(anchor(id, 0));
    return id;
  };

  const convert = (id: string, kind: 'paragraph' | 'heading' | 'quote' | 'list-item'): void => {
    mustBeEditable('convert');
    const block = blockById(id);
    if (!block || !textBlock(block)) throw new TypeError(`block ${id} is not a text block`);
    if (block.kind === kind) return;
    const text = textOf(block);
    if (kind === 'list-item') {
      if (!value.roots.includes(id)) throw new TypeError(`block ${id} is not a root`);
      const list = options.createBlockId();
      const roots = value.roots.map((rootId) => rootId === id ? list : rootId);
      const blocks = value.blocks.map((candidate) => candidate.id === id
        ? ({ id, kind: 'list-item', text } as DocumentBlock) : candidate);
      publish({ ...structural(value), roots,
        blocks: [...blocks, { id: list, kind: 'list', children: [id] } as DocumentBlock] }, id, text.length);
    } else {
      const made: DocumentBlock = kind === 'heading'
        ? ({ id, kind: 'heading', level: 2, text } as DocumentBlock)
        : kind === 'quote'
          ? ({ id, kind: 'quote', text } as DocumentBlock)
          : ({ id, kind: 'paragraph', text } as DocumentBlock);
      let roots = [...value.roots];
      let blocks = value.blocks.map((candidate) => candidate.id === id ? made : candidate);
      if (block.kind === 'list-item') {
        const list = value.blocks.find((candidate) => candidate.kind === 'list' && candidate.children?.includes(id));
        if (list) {
          const remaining = (list.children ?? []).filter((child) => child !== id);
          const at = roots.indexOf(list.id);
          if (remaining.length === 0) {
            roots = roots.map((rootId) => rootId === list.id ? id : rootId);
            blocks = blocks.filter((candidate) => candidate.id !== list.id);
          } else {
            roots.splice(at + 1, 0, id);
            blocks = blocks.map((candidate) => candidate.id === list.id ? { ...candidate, children: remaining } : candidate);
          }
        }
      }
      publish({ ...structural(value), roots, blocks }, id, text.length);
    }
    paint();
    restore(anchor(id, text.length));
  };

  const onKeyDown = (event: Event) => {
    const keyboard = event as KeyboardEvent;
    if (keyboard.key !== 'Enter' || keyboard.shiftKey || keyboard.isComposing) return;
    const id = idFrom(event.target);
    const block = id && blockById(id);
    if (!id || !block || !textBlock(block)) return;
    event.stopPropagation();
    keyboard.preventDefault();
    const selection = root.ownerDocument.defaultView?.getSelection();
    const [from, to] = selectedRange(event.target as Element, selection);
    const before = textOf(block).slice(0, from);
    const after = textOf(block).slice(to);
    const id2 = options.createBlockId();
    const nextBlock: DocumentBlock = block.kind === 'list-item'
      ? { id: id2, kind: 'list-item', text: after }
      : { id: id2, kind: 'paragraph', text: after };
    const blocks = value.blocks.map((candidate) => candidate.id === id
      ? { ...candidate, text: before } : candidate);
    if (block.kind === 'list-item') {
      const parent = value.blocks.find((candidate) => candidate.children?.includes(id));
      if (!parent?.children) return;
      const children = [...parent.children];
      children.splice(children.indexOf(id) + 1, 0, id2);
      publish({ ...structural(value), blocks: [...blocks.map((candidate) => candidate.id === parent.id
        ? { ...candidate, children } : candidate), nextBlock] }, id2, 0);
    } else {
      const roots = [...value.roots];
      roots.splice(roots.indexOf(id) + 1, 0, id2);
      publish({ ...structural(value), roots, blocks: [...blocks, nextBlock] }, id2, 0);
    }
    paint();
    restore(anchor(id2, 0));
  };

  const reorder = (block: string, before?: string) => {
    mustBeEditable('reorder');
    const active = root.ownerDocument.activeElement;
    const activeId = idFrom(active);
    const selection = root.ownerDocument.defaultView?.getSelection();
    const caret = activeId ? anchor(activeId,
      selection && selection.rangeCount ? selection.getRangeAt(0).startOffset : 0) : undefined;
    const roots = value.roots.filter((id) => id !== block);
    if (roots.length === value.roots.length) throw new TypeError(`block ${block} is not a root`);
    const at = before === undefined ? roots.length : roots.indexOf(before);
    if (at < 0) throw new TypeError(`block ${before} is not a root`);
    roots.splice(at, 0, block);
    publish({ ...structural(value), roots }, block, 0);
    paint();
    if (caret) restore(caret);
  };

  const restore = (saved: DocumentCaretAnchor): boolean => {
    // Answers false rather than throwing: a caret is a question about editing,
    // and "there is no caret here" is a true answer, not a caller's mistake.
    // The publish path's own failure repaint calls this, so throwing would turn
    // a refused write into a second failure.
    if (!editable) return false;
    if (saved.edit.object !== options.edit.object || saved.edit.role !== options.edit.role) return false;
    const resolved = blockById(saved.block) ? saved.block
      : saved.affinity === 'backward' ? saved.before ?? saved.after : saved.after ?? saved.before;
    if (!resolved) return false;
    const el = [...root.querySelectorAll(`[${BLOCK}]`)]
      .find((candidate) => candidate.getAttribute(BLOCK) === resolved);
    if (!el || el.getAttribute('contenteditable') !== 'true') return false;
    const selection = root.ownerDocument.defaultView?.getSelection();
    if (!selection) return false;
    const range = root.ownerDocument.createRange();
    const node = el.firstChild ?? el;
    range.setStart(node, Math.min(saved.offset, node.textContent?.length ?? 0));
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    (el as HTMLElement).focus();
    return true;
  };

  // THE HOST MUST NOT CLAIM THESE BLOCKS. Mounted inside the same container the
  // edit host watches, every keystroke here was cancelled by it — measured on
  // the ordinary URL as "Not saved — this value is read-only in this lens."
  // Enter still worked, which is what made it look fine: the canvas cancels
  // Enter at `keydown`, so no `beforeinput` was ever raised to be refused.
  root.setAttribute(OWNS_EDITING_ATTR, 'document-canvas');
  /**
   * AND THE ROOT MUST STOP BEING EDITABLE ITSELF.
   *
   * The canvas mounts into the generated lens's own body field, which is a
   * `contenteditable` element. Nesting editable blocks inside it means the
   * browser raises `input` on the OUTER element — it is the editing host of
   * that subtree — and the outer element carries no block attribute, so every
   * keystroke was read as "not one of mine" and dropped. Measured on the
   * ordinary URL: the text changed on screen, `input` fired as
   * `DIV:NO-BLOCK-ATTR`, and the store never heard about it.
   *
   * Restored on `destroy`, because the field is the lens's when the canvas is
   * not there.
   */
  const inheritedEditingAttributes = new Map(
    ['contenteditable', 'role', 'aria-multiline', 'data-morph-empty']
      .map((name) => [name, root.getAttribute(name)] as const));
  for (const name of inheritedEditingAttributes.keys()) root.removeAttribute(name);
  // Not registered at all in read-only mode, rather than registered and
  // guarded. A listener that runs and decides to do nothing still consumes the
  // event and still has to be right about `stopPropagation`; one that was never
  // added cannot be wrong about either.
  if (editable) {
    root.addEventListener('input', onInput);
    root.addEventListener('keydown', onKeyDown);
  }
  paint();
  return {
    content: () => value,
    reconcile,
    insert,
    convert,
    reorder,
    restore,
    destroy: () => {
      // `removeEventListener` for a listener that was never added is a no-op,
      // so teardown stays one path rather than two.
      root.removeEventListener('input', onInput);
      root.removeEventListener('keydown', onKeyDown);
      root.removeAttribute(OWNS_EDITING_ATTR);
      for (const [name, inherited] of inheritedEditingAttributes) {
        if (inherited !== null) root.setAttribute(name, inherited);
      }
    },
  };
}
